import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import Select from "react-select";
import { Button, Col, Input, Row, Spinner } from "reactstrap";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const DataFlowRow = ({
  row,
  assetOptions,
  dataTypeOptions,
  getSubcategoryOptions,
  requiresSubcategory,
  requiresTextSubcategory,
  onSave,
  onDelete,
  isSaving = false,
  isDeleting = false,
  t,
}) => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedDataType, setSelectedDataType] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [errors, setErrors] = useState({});
  const [otherSubcategoryText, setOtherSubcategoryText] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);


  useEffect(() => {
    const assetOption =
      assetOptions?.find((option) => option.value === row?.assetId) || null;

    const dataTypeOption =
      dataTypeOptions?.find((option) => option.value === row?.dataType) || null;
      
    const subcategoryOptions = dataTypeOption
      ? getSubcategoryOptions(dataTypeOption.value)
      : [];

    const subcategoryOption =
      subcategoryOptions?.find((option) => option.value === row?.subcategory) ||
      null;

    setSelectedAsset(assetOption);
    setSelectedDataType(dataTypeOption);
    setSelectedSubcategory(subcategoryOption);
    if (dataTypeOption?.value === "Other") {
      setOtherSubcategoryText(row?.subcategory || "");
    } else {
      setOtherSubcategoryText("");
    }
    setErrors({});
  }, [row, assetOptions, dataTypeOptions, getSubcategoryOptions, t]);

  const subcategoryOptions = useMemo(() => {
    if (!selectedDataType) return [];
    return getSubcategoryOptions(selectedDataType.value);
  }, [selectedDataType, getSubcategoryOptions]);

  const shouldShowSubcategory = selectedDataType
    ? requiresSubcategory(selectedDataType.value)
    : false;

  const shouldShowOtherSubcategory = selectedDataType
    ? requiresTextSubcategory(selectedDataType.value)
    : false;

  const handleDataTypeChange = (option) => {
    setSelectedDataType(option);

    if (!option || !requiresSubcategory(option.value)) {
      setSelectedSubcategory(null);
    }

    if (!option || !requiresTextSubcategory(option.value)) {
      setOtherSubcategoryText("");
    }

    setErrors((prev) => ({
      ...prev,
      dataType: null,
      subcategory: null,
    }));
  };

  const handleOtherSubcategoryChange = (e) => {
    setOtherSubcategoryText(e.target.value);
    setErrors((prev) => ({
      ...prev,
      subcategory: null,
    }));
  };

  const handleAssetChange = (option) => {
    setSelectedAsset(option);
    setErrors((prev) => ({
      ...prev,
      assetId: null,
    }));
  };

  const handleSubcategoryChange = (option) => {
    setSelectedSubcategory(option);
    setErrors((prev) => ({
      ...prev,
      subcategory: null,
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!selectedAsset?.value) {
      nextErrors.assetId = t("Application is required.");
    }

    if (!selectedDataType?.value) {
      nextErrors.dataType = t("Type of Data is required.");
    }

    if (
      selectedDataType?.value &&
      requiresSubcategory(selectedDataType.value) &&
      !selectedSubcategory?.value
    ) {
      nextErrors.subcategory = t("Data Category is required.");
    }

    if (
      selectedDataType?.value &&
      requiresTextSubcategory(selectedDataType.value) &&
      !otherSubcategoryText.trim()
    ) {
      nextErrors.subcategory = t("Data Category is required.");
    }
    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    onSave({
      id: row?.id || null,
      assetId: selectedAsset.value,
      dataType: selectedDataType.baseLabel,
      subcategory: shouldShowOtherSubcategory
        ? otherSubcategoryText.trim()
        : selectedSubcategory?.baseLabel || null,
    });
  };


  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete(row);
  };

  const handleCloseDeleteModal = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
    }
  }, [isDeleting]);

  return (
    <div className=" rounded p-3 bg-white">
      <DeleteConfirmationModal
        isDeleteModalOpen={isDeleteModalOpen}
        handleCloseDeleteModal={handleCloseDeleteModal}
        isDeleting={isDeleting}
        handleConfirmDelete={handleConfirmDelete}
      />
      <Row className="g-3 align-items-start">
        <Col xs={12} lg={11}>
          <Row>
            <Col xs={12} lg={4}>
              <Select
                options={assetOptions}
                value={selectedAsset}
                onChange={handleAssetChange}
                placeholder={t("Select...")}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
              />
              {errors.assetId && (
                <div className="invalid-feedback d-block">{errors.assetId}</div>
              )}
            </Col>

            <Col xs={12} lg={4}>
              <Select
                options={dataTypeOptions}
                value={selectedDataType}
                onChange={handleDataTypeChange}
                placeholder={t("Select...")}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
              />
              {errors.dataType && (
                <div className="invalid-feedback d-block">
                  {errors.dataType}
                </div>
              )}
            </Col>

            <Col xs={12} lg={4}>
              {shouldShowSubcategory ? (
                <>
                  <Select
                    options={subcategoryOptions}
                    value={selectedSubcategory}
                    onChange={handleSubcategoryChange}
                    placeholder={t("Select...")}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                  />
                  {errors.subcategory && (
                    <div className="invalid-feedback d-block">
                      {errors.subcategory}
                    </div>
                  )}
                </>
              ) : shouldShowOtherSubcategory ? (
                <>
                  <Input
                    type="text"
                    value={otherSubcategoryText}
                    onChange={handleOtherSubcategoryChange}
                    placeholder={t("Enter subcategory")}
                  />
                  {errors.subcategory && (
                    <div className="invalid-feedback d-block">
                      {errors.subcategory}
                    </div>
                  )}
                </>
              ) : (
                <div className="form-control bg-light text-muted">
                  {t("Not required")}
                </div>
              )}
            </Col>
          </Row>
        </Col>
        <Col xs={12} lg={1}>
          <div className="data-flow__row-actions mt-1 gap-1">
            <Button
              color="link"
              className="data-flow__icon-btn data-flow__icon-btn--save"
              onClick={handleSave}
              disabled={isSaving || isDeleting}
              title={t("Save")}
            >
              {isSaving ? <Spinner size="sm" /> : <FaCheck />}
            </Button>
            <Button
              color="link"
              className="data-flow__icon-btn data-flow__icon-btn--delete"
              onClick={handleDelete}
              disabled={isDeleting || isSaving}
              title={t("Delete")}
            >
              {isDeleting ? <Spinner size="sm" /> : <RiDeleteBin6Line />}
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default withNamespaces()(DataFlowRow);
